// Jun-2026 — PO Payment Requests: raise a request against an Approved PO
// (pick Approved GRNs + route to a same-branch active user), inbox panel with
// complete-by-PI-link / reject / cancel actions. Dedicated permission
// `po_payment_request` (create = raise, view = inbox & actions as assignee).
import React, { useEffect, useState } from "react";
import { usePager, Paginator } from "@/components/Paginator";
import { api, formatINR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Ban, CheckCircle2, Lock, Search, FilePlus2, Eye } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useFormTab } from "@/hooks/useFormTab";
import { InvoiceModal } from "@/pages/InvoicePage";

const inputCls = "w-full border border-slate-300 rounded px-2 py-1.5 text-xs";
const REQ_BADGE = {
  Pending: "bg-amber-100 text-amber-800 border-amber-300",
  "PI Created": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "PI Approved": "bg-indigo-100 text-indigo-800 border-indigo-300",
  Rejected: "bg-rose-100 text-rose-800 border-rose-300",
  Cancelled: "bg-slate-200 text-slate-600 border-slate-300",
};
// Jun-2026 (user spec) — display label only: a raised request reads
// "PI Requested" (DB status stays "Pending" for all existing queries).
const REQ_LABEL = { Pending: "PI Requested" };
// Jun-2026 — live payment state of the linked PI (derived server-side).
const PAYSTATE_BADGE = {
  Paid: "bg-emerald-600 text-white border-emerald-700",
  "Partially Paid": "bg-amber-100 text-amber-800 border-amber-300",
  Posted: "bg-sky-100 text-sky-800 border-sky-300",
};
const inr = (v) => (v == null ? "—" : formatINR(v));

export function RequestPaymentModal({ project, po, onClose, onDone }) {
  const [grns, setGrns] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [sel, setSel] = useState({});
  const [to, setTo] = useState([]);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  // Jun-2026 — PO balance guard + explicit requested amount.
  const [bal, setBal] = useState(null);
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  // Jun-2026 — optional Category 1 / 2 (GRN section masters).
  const [cats, setCats] = useState({ cat1: [], cat2: [] });
  const [cat1, setCat1] = useState("");
  const [cat2, setCat2] = useState("");
  useEffect(() => {
    api.get(`/projects/${project.id}/pos/${po.po_id}/payable-grns`)
      .then((r) => setGrns(r.data.grns || []))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load GRNs"); onClose(); });
    api.get(`/projects/${project.id}/payment-request-recipients`)
      .then((r) => setRecipients(r.data.recipients || [])).catch(() => {});
    api.get(`/projects/${project.id}/pos/${po.po_id}/payment-request-balance`)
      .then((r) => setBal(r.data)).catch(() => setBal(null));
    api.get("/grn-categories").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setCats({ cat1: list.filter((x) => x.kind === "cat1"), cat2: list.filter((x) => x.kind === "cat2") });
    }).catch(() => {});
  }, [project.id, po.po_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const chosenTotal = (grns || []).filter((g) => sel[g.id])
    .reduce((s, g) => s + Number(g.provisional_total || 0), 0);
  useEffect(() => {
    if (!amountTouched) setAmount(chosenTotal > 0 ? String(Math.round(chosenTotal * 100) / 100) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenTotal]);
  const exhausted = bal != null && bal.balance != null && bal.balance <= 0;
  const overBalance = bal?.balance != null && Number(amount || 0) > bal.balance + 0.005;

  const submit = async () => {
    if (!to.length) return toast.error("Add at least one user who can create the PI");
    if (!Number(amount) && chosenTotal <= 0) return toast.error("Enter the requested amount");
    if (overBalance) return toast.error(`Only ${inr(bal.balance)} of this PO remains available`);
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/po-payment-requests`,
        { po_id: po.po_id, grn_ids: chosen, assigned_to_ids: to.map((u) => u.id),
          remark, requested_amount: Number(amount) || 0,
          category1_id: cat1, category2_id: cat2 });
      toast.success(`${r.data.request_number} sent`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="pay-request-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 text-xs">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-sm font-bold text-slate-900">Request Payment — {po.po_number}</div>
            <div className="text-[10px] text-slate-500">{po.vendor_name} · select the Approved GRN(s) to be paid</div>
          </div>
          <button data-testid="pay-request-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {bal != null && bal.po_value != null && (
            <div className={`flex flex-wrap gap-x-4 gap-y-1 border rounded px-3 py-2 text-[10px] font-bold ${exhausted ? "border-rose-300 bg-rose-50 text-rose-800" : "border-sky-200 bg-sky-50 text-sky-900"}`} data-testid="pay-request-balance-strip">
              <span>PO Value: <span className="tabular-nums">{inr(bal.po_value)}</span></span>
              <span>Requested / PI Created: <span className="tabular-nums">{inr(bal.consumed)}</span></span>
              <span>Available Balance: <span className="tabular-nums" data-testid="pay-request-balance-value">{inr(bal.balance)}</span></span>
              {exhausted && <span className="w-full font-black uppercase tracking-wider">PO fully consumed — no further payment request allowed</span>}
            </div>
          )}
          <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="pay-request-grn-list">
            {grns === null && <div className="px-3 py-4 text-center text-slate-400">Loading GRNs…</div>}
            {grns && grns.length === 0 && <div className="px-3 py-4 text-center text-slate-400" data-testid="pay-request-no-grns">No Approved GRNs against this PO — you can still raise the request without GRN.</div>}
            {(grns || []).map((g) => (
              <label key={g.id} className={`flex items-center gap-2 px-3 py-2 ${g.locked ? "opacity-60" : "cursor-pointer hover:bg-slate-50"}`}
                data-testid={`pay-request-grn-${g.grn_number?.replace(/\//g, "-")}`}>
                <input type="checkbox" disabled={g.locked} checked={!!sel[g.id]}
                  onChange={(e) => setSel((s) => ({ ...s, [g.id]: e.target.checked }))} />
                <span className="font-mono font-bold">{g.grn_number}</span>
                <span className="text-slate-500">{formatDate(g.grn_date)}</span>
                {g.challan_no && <span className="text-slate-400">Challan {g.challan_no}</span>}
                <span className="ml-auto tabular-nums">{inr(g.provisional_total)}</span>
                {g.locked && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold text-rose-600" title={`Committed to ${g.locked_by}`}>
                    <Lock size={10} /> {g.locked_by}
                  </span>
                )}
              </label>
            ))}
          </div>
          <div className="space-y-1">
            <span className="font-bold text-slate-600 text-xs">Requested Amount (₹) *</span>
            <input type="number" min="0" step="any" data-testid="pay-request-amount"
              className={`${inputCls} ${overBalance ? "border-rose-400 bg-rose-50/50" : ""}`}
              placeholder={bal?.balance != null ? `Max ${inr(bal.balance)}` : "Amount to be paid"}
              value={amount}
              onChange={(e) => { setAmountTouched(true); setAmount(e.target.value); }} />
            {overBalance && <span className="text-[9px] font-bold text-rose-600 block" data-testid="pay-request-over-balance">Exceeds the available PO balance of {inr(bal.balance)}</span>}
            {chosenTotal > 0 && <span className="text-[9px] text-slate-400 block">Auto-filled from the selected GRN(s) — editable.</span>}
          </div>
          <div className="space-y-1">
            <span className="font-bold text-slate-600 text-xs">Send to (creates the PI) * <span className="font-normal text-slate-400">— searchable · add multiple, any one can act</span></span>
            <SearchableSelect testid="pay-request-recipient" className="w-full"
              items={recipients.filter((u) => !to.find((t) => t.id === u.id))} value=""
              onChange={(id, row) => { if (row) setTo((t) => [...t, row]); }}
              getLabel={(u) => `${u.name || u.email} · ${u.role}`}
              getSubLabel={(u) => u.email}
              getSearchText={(u) => `${u.name || ""} ${u.email || ""} ${u.role || ""}`}
              placeholder="+ Add user…" searchPlaceholder="Search user by name / email / role…" />
            {to.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="pay-request-recipient-chips">
                {to.map((u) => (
                  <span key={u.id} className="text-[10px] bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 text-sky-800 inline-flex items-center gap-1" data-testid={`pay-request-chip-${u.id}`}>
                    {u.name || u.email}
                    <button data-testid={`pay-request-chip-remove-${u.id}`} onClick={() => setTo((t) => t.filter((x) => x.id !== u.id))} className="text-rose-600"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            <span className="text-[9px] text-slate-400 block">Active users of this project's branch. Pick people whose role can create Purchase Invoices — any ONE of them completes the request. GRN selection above is optional (leave empty for POs without GRN).</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="font-bold text-slate-600 text-xs">Category 1 <span className="font-normal text-slate-400">(optional)</span></span>
              <select data-testid="pay-request-cat1" className={inputCls} value={cat1} onChange={(e) => setCat1(e.target.value)}>
                <option value="">—</option>
                {cats.cat1.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <span className="font-bold text-slate-600 text-xs">Category 2 <span className="font-normal text-slate-400">(optional)</span></span>
              <select data-testid="pay-request-cat2" className={inputCls} value={cat2} onChange={(e) => setCat2(e.target.value)}>
                <option value="">—</option>
                {cats.cat2.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <textarea data-testid="pay-request-remark" className={`${inputCls} h-14`} placeholder="Remark (optional)"
            value={remark} onChange={(e) => setRemark(e.target.value)} />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded font-bold text-slate-600">Close</button>
            <button data-testid="pay-request-submit" disabled={busy || exhausted} onClick={submit}
              className="px-3 py-1.5 bg-[#F97316] text-white font-bold rounded inline-flex items-center gap-1.5 disabled:opacity-50">
              <Send size={12} /> Send Request{chosen.length ? ` (${chosen.length} GRN${chosen.length > 1 ? "s" : ""})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompleteModal({ project, req, onClose, onDone }) {
  const [pis, setPis] = useState(null);
  const [piId, setPiId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/projects/${project.id}/pos/${req.po_id}/linkable-pis`)
      .then((r) => setPis(r.data.pis || [])).catch(() => setPis([]));
  }, [project.id, req.po_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (!piId) return toast.error("Select the PI you created");
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/po-payment-requests/${req.id}/complete`, { pi_id: piId });
      toast.success("Request completed — PI linked");
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Completion failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="pay-complete-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 space-y-3 text-xs">
        <div className="text-sm font-bold text-slate-900">Mark PI Created — {req.request_number}</div>
        <div className="text-[10px] text-slate-500">Create the PI in the Purchase Invoice module first, then link it here. This closes the request and permanently locks the GRNs.</div>
        <select data-testid="pay-complete-pi-select" className={inputCls} value={piId} onChange={(e) => setPiId(e.target.value)}>
          <option value="">{pis === null ? "Loading PIs…" : pis.length ? "— select PI —" : "No PIs found against this PO"}</option>
          {(pis || []).map((p) => (
            <option key={p.id} value={p.id}>{p.invoice_number} · {formatDate(p.invoice_date)} · {p.status}{p.net_payable != null ? ` · ${formatINR(p.net_payable)}` : ""}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded font-bold text-slate-600">Close</button>
          <button data-testid="pay-complete-submit" disabled={busy} onClick={submit}
            className="px-3 py-1.5 bg-emerald-700 text-white font-bold rounded inline-flex items-center gap-1.5 disabled:opacity-50">
            <CheckCircle2 size={12} /> Link PI &amp; Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function PaymentRequestsPanel({ project, refreshKey, onChanged }) {
  const openFormTab = useFormTab();
  const [data, setData] = useState(null);
  const [complete, setComplete] = useState(null);
  const [view, setView] = useState(null);
  const [q, setQ] = useState("");
  const filteredRows = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return data?.rows || [];
    const hit = (v) => String(v || "").toLowerCase().includes(t);
    return (data?.rows || []).filter((r) =>
      hit(r.request_number) || hit(r.po_number) || hit(r.vendor_name)
      || hit(r.status) || hit(r.pi_number) || hit(r.requested_by)
      || hit(r.assigned_to_display) || hit(r.remark)
      || hit(r.category1_name) || hit(r.category2_name) || hit(r.payment_status)
      || hit(r.rejection_reason) || (r.grn_numbers || []).some(hit));
  }, [data, q]);
  const reqPager = usePager(filteredRows, 10);
  const load = () => {
    api.get(`/projects/${project.id}/po-payment-requests`)
      .then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  };
  useEffect(load, [project.id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (req, action) => {
    let body = {};
    if (action === "reject") {
      const reason = window.prompt(`Rejection reason for ${req.request_number} (mandatory — sent to requester):`);
      if (reason === null) return;
      body = { reason };
    } else if (action === "cancel") {
      if (!window.confirm(`Cancel ${req.request_number}? The GRNs become available again.`)) return;
    }
    try {
      await api.post(`/projects/${project.id}/po-payment-requests/${req.id}/${action}`, body);
      toast.success(action === "reject" ? "Request rejected" : "Request cancelled");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  // Jun-2026 — "Create PI" shortcut: opens a New Purchase Invoice tab
  // pre-filled from the request's PO (remaining qty) + linked PPR number.
  const createPi = (r) => openFormTab({
    path: `/purchase-invoices/new/ppr-${r.request_number}`,
    label: `New Invoice · ${r.request_number}`,
    parent: "/purchase-invoices",
    onSaved: () => { load(); onChanged && onChanged(); },
    render: ({ close, saved, viewList }) => (
      <InvoiceModal type="purchase" invoice={null}
        prefilled={{ kind: "FROM_PPR", request: { ...r, project_id: project.id } }}
        onClose={close} onSaved={saved} onViewList={viewList} />
    ),
  });

  if (!data) return null;

  // Shared row actions — desktop table cell AND mobile card.
  const reqActions = (r) => (
    <>
      <button data-testid={`payment-request-view-${r.request_number}`} onClick={() => setView(r)} title="View request details"
        className="text-slate-500 hover:text-slate-900 mr-2 inline-flex items-center align-middle"><Eye size={13} /></button>
      {r.can_complete && (
        <button data-testid={`payment-request-createpi-${r.request_number}`} onClick={() => createPi(r)}
          className="text-sky-700 hover:underline font-bold mr-2 inline-flex items-center gap-0.5"><FilePlus2 size={11} /> Create PI</button>
      )}
      {r.can_complete && (
        <button data-testid={`payment-request-complete-${r.request_number}`} onClick={() => setComplete(r)}
          className="text-emerald-700 hover:underline font-bold mr-2 inline-flex items-center gap-0.5"><CheckCircle2 size={11} /> PI Created</button>
      )}
      {r.can_reject && (
        <button data-testid={`payment-request-reject-${r.request_number}`} onClick={() => act(r, "reject")}
          className="text-rose-700 hover:underline font-bold mr-2 inline-flex items-center gap-0.5"><Ban size={11} /> Reject</button>
      )}
      {r.can_cancel && (
        <button data-testid={`payment-request-cancel-${r.request_number}`} onClick={() => act(r, "cancel")}
          className="text-slate-500 hover:underline font-bold">Cancel</button>
      )}
    </>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="payment-requests-panel">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Payment Requests</div>
        <div className="relative w-full sm:w-72 order-last sm:order-none sm:ml-2 sm:mr-auto">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="payment-requests-search" className="border border-slate-300 rounded pl-6 pr-2 py-1 text-[11px] w-full"
            placeholder="Search request / PO / GRN / PI / user / status…"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {data.pending_for_me > 0 && (
          <span className="ml-auto text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5" data-testid="payment-requests-mine-badge">
            {data.pending_for_me} awaiting your PI
          </span>
        )}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-xs" data-testid="payment-requests-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100">
              <th className="px-2 py-1.5">Request</th>
              <th className="px-2 py-1.5">PO</th>
              <th className="px-2 py-1.5">Vendor</th>
              <th className="px-2 py-1.5">Category 1</th>
              <th className="px-2 py-1.5">Category 2</th>
              <th className="px-2 py-1.5">GRNs</th>
              <th className="px-2 py-1.5 text-right">GRN Value</th>
              <th className="px-2 py-1.5 text-right">Requested Amt</th>
              <th className="px-2 py-1.5">Requested By</th>
              <th className="px-2 py-1.5">Assigned To</th>
              <th className="px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5">PI</th>
              <th className="px-2 py-1.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reqPager.slice.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`payment-request-row-${r.request_number}`}>
                <td className="px-2 py-1.5">
                  <div className="font-mono font-bold">{r.request_number}</div>
                  <div className="text-[9px] text-slate-400">{formatDate(r.created_at)}</div>
                </td>
                <td className="px-2 py-1.5 font-mono">{r.po_number}</td>
                <td className="px-2 py-1.5 max-w-[160px]"><span className="truncate block" title={r.vendor_name} data-testid={`payment-request-vendor-${r.request_number}`}>{r.vendor_name || "—"}</span></td>
                <td className="px-2 py-1.5 max-w-[120px]"><span className="truncate block" title={r.category1_name} data-testid={`payment-request-cat1-${r.request_number}`}>{r.category1_name || "—"}</span></td>
                <td className="px-2 py-1.5 max-w-[120px]"><span className="truncate block" title={r.category2_name} data-testid={`payment-request-cat2-${r.request_number}`}>{r.category2_name || "—"}</span></td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{(r.grn_numbers || []).length ? r.grn_numbers.join(", ") : <span className="font-sans text-slate-400">no GRN</span>}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.grn_total)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-bold" data-testid={`payment-request-amount-${r.request_number}`}>{inr(r.requested_amount)}</td>
                <td className="px-2 py-1.5 max-w-[140px] truncate">{r.requested_by}</td>
                <td className="px-2 py-1.5 max-w-[170px]"><span className="truncate block" title={r.assigned_to_display}>{r.assigned_to_display}</span>{r.assigned_to_me && <span className="text-[9px] font-bold text-sky-700">(includes you)</span>}</td>
                <td className="px-2 py-1.5">
                  <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${REQ_BADGE[r.status] || ""}`}>{REQ_LABEL[r.status] || r.status}</span>
                  {r.payment_status && (
                    <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ml-1 ${PAYSTATE_BADGE[r.payment_status] || ""}`}
                      data-testid={`payment-request-paystate-${r.request_number}`}>{r.payment_status}</span>
                  )}
                  {r.status === "Rejected" && r.rejection_reason && <div className="text-[9px] text-rose-600 max-w-[160px]" title={r.rejection_reason}>{r.rejection_reason}</div>}
                </td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{r.pi_number || "—"}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">{reqActions(r)}</td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr><td colSpan={13} className="px-2 py-5 text-center text-slate-400" data-testid="payment-requests-empty">No payment requests match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked request cards */}
      <div className="md:hidden divide-y divide-slate-100" data-testid="payment-requests-cards">
        {reqPager.slice.map((r) => (
          <div key={r.id} className="p-3 space-y-1.5" data-testid={`payment-request-card-${r.request_number}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-bold text-[11px]">{r.request_number}</span>
              <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${REQ_BADGE[r.status] || ""}`}>{REQ_LABEL[r.status] || r.status}</span>
              {r.payment_status && (
                <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${PAYSTATE_BADGE[r.payment_status] || ""}`}>{r.payment_status}</span>
              )}
              <span className="ml-auto text-[10px] text-slate-400">{formatDate(r.created_at)}</span>
            </div>
            <div className="text-xs text-slate-700 break-words">{r.vendor_name || "—"} · <span className="font-mono text-[10px]">{r.po_number}</span></div>
            <div className="text-[11px] flex flex-wrap gap-x-3 gap-y-0.5">
              <span className="text-slate-500">GRN Value: <b className="tabular-nums text-slate-800">{inr(r.grn_total)}</b></span>
              <span className="text-slate-500">Requested: <b className="tabular-nums text-slate-800">{inr(r.requested_amount)}</b></span>
            </div>
            {(r.category1_name || r.category2_name) && (
              <div className="text-[11px] flex flex-wrap gap-x-3 gap-y-0.5">
                <span className="text-slate-500">Cat 1: <b className="text-slate-800">{r.category1_name || "—"}</b></span>
                <span className="text-slate-500">Cat 2: <b className="text-slate-800">{r.category2_name || "—"}</b></span>
              </div>
            )}
            {(r.grn_numbers || []).length > 0 && (
              <div className="text-[10px] font-mono text-slate-500 break-words">GRN: {r.grn_numbers.join(", ")}</div>
            )}
            <div className="text-[10px] text-slate-500 break-all">
              By {r.requested_by} → {r.assigned_to_display}
              {r.assigned_to_me && <span className="text-sky-700 font-bold"> (includes you)</span>}
            </div>
            {r.pi_number && <div className="text-[10px] font-mono text-slate-600">PI: {r.pi_number}</div>}
            {r.status === "Rejected" && r.rejection_reason && (
              <div className="text-[10px] text-rose-600 break-words">{r.rejection_reason}</div>
            )}
            <div className="flex flex-wrap items-center gap-y-1.5 pt-0.5">{reqActions(r)}</div>
          </div>
        ))}
        {filteredRows.length === 0 && (
          <div className="p-4 text-center text-slate-400 text-xs">No payment requests match.</div>
        )}
      </div>
      <Paginator pager={reqPager} testid="payment-requests-pager" />
      {complete && (
        <CompleteModal project={project} req={complete}
          onClose={() => setComplete(null)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}
      {view && (
        <RequestViewModal project={project} req={view} onClose={() => setView(null)} />
      )}
    </div>
  );
}

// Aug-2026 (user request) — eye/view modal: every request entry + remark +
// status-history timeline.
const HIST_LABEL = {
  created: "Request raised", pi_created: "PI created",
  pi_approved: "PI approved", rejected: "Rejected", cancelled: "Cancelled",
};
function RequestViewModal({ project, req, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/projects/${project.id}/po-payment-requests/${req.id}`)
      .then((r) => setD(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load request"); setD(req); });
  }, [project.id, req.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const r = d || req;
  const Field = ({ label, children, testid }) => (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="text-xs text-slate-800 break-words">{children ?? "—"}</div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto py-8" data-testid="payment-request-view-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-sm">{r.request_number}</span>
            <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${REQ_BADGE[r.status] || ""}`}>{REQ_LABEL[r.status] || r.status}</span>
            {r.payment_status && (
              <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${PAYSTATE_BADGE[r.payment_status] || ""}`}>{r.payment_status}</span>
            )}
          </div>
          <button data-testid="payment-request-view-close" onClick={onClose} className="text-slate-500 hover:text-slate-800 shrink-0"><X size={15} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5">
            <Field label="PO" testid="prv-po"><span className="font-mono">{r.po_number}</span></Field>
            <Field label="Vendor" testid="prv-vendor">{r.vendor_name || "—"}</Field>
            <Field label="PI" testid="prv-pi">{r.pi_number ? <span className="font-mono">{r.pi_number}</span> : "—"}</Field>
            <Field label="Category 1" testid="prv-cat1">{r.category1_name || "—"}</Field>
            <Field label="Category 2" testid="prv-cat2">{r.category2_name || "—"}</Field>
            <Field label="GRNs" testid="prv-grns">{(r.grn_numbers || []).length ? <span className="font-mono text-[10px]">{r.grn_numbers.join(", ")}</span> : "no GRN"}</Field>
            <Field label="GRN Value" testid="prv-grn-value"><span className="tabular-nums">{inr(r.grn_total)}</span></Field>
            <Field label="Requested Amount" testid="prv-amount"><b className="tabular-nums">{inr(r.requested_amount)}</b></Field>
            <Field label="Raised On" testid="prv-created">{formatDate(r.created_at)}</Field>
            <Field label="Requested By" testid="prv-by">{r.requested_by}</Field>
            <Field label="Assigned To" testid="prv-assigned">{r.assigned_to_display || "—"}</Field>
            <Field label="Last Updated" testid="prv-updated">{formatDate(r.updated_at)}</Field>
          </div>
          <div data-testid="prv-remark">
            <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Remark</div>
            <div className={`text-xs rounded border px-2.5 py-2 mt-0.5 break-words ${r.remark ? "bg-amber-50 border-amber-200 text-slate-800" : "bg-slate-50 border-slate-200 text-slate-400"}`}>
              {r.remark || "No remark given"}
            </div>
          </div>
          {r.rejection_reason && (
            <div data-testid="prv-rejection">
              <div className="text-[9px] uppercase tracking-wider font-bold text-rose-500">Rejection Reason</div>
              <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2.5 py-2 mt-0.5 break-words">{r.rejection_reason}</div>
            </div>
          )}
          {(d?.status_history || []).length > 0 && (
            <div data-testid="prv-timeline">
              <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-1">Timeline</div>
              <div className="space-y-1.5 border-l-2 border-slate-200 pl-3">
                {d.status_history.map((h, i) => (
                  <div key={i} className="text-[11px]">
                    <span className="font-bold text-slate-800">{HIST_LABEL[h.action] || h.action}</span>
                    {h.detail ? <span className="text-slate-600"> — {h.detail}</span> : null}
                    <div className="text-[10px] text-slate-400">{formatDate(h.at)} · {h.by}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
